import {useEffect, useState} from 'react'
import {Button, Form, Spinner} from 'react-bootstrap'
import {TipTapEditor} from './TipTapEditor'

export interface IEmailSettingsValues {
  senderName?: string | null
  senderEmail?: string | null
  signatureHtml?: string | null
}

interface IResolvedSources {
  senderName?: string | null
  senderEmail?: string | null
  signatureHtml?: string | null
}

interface IResolvedEmailSettings extends IEmailSettingsValues {
  sources?: IResolvedSources
}

interface Props {
  values: IEmailSettingsValues | null | undefined
  resolved?: IResolvedEmailSettings | null
  showInheritance?: boolean
  saving?: boolean
  onSave: (input: IEmailSettingsValues) => void | Promise<void>
}

const SOURCE_LABEL: Record<string, string> = {
  system: 'Sistema',
  organization: 'Organización',
  user: 'Usuario',
}

const FIELDS: {key: keyof IEmailSettingsValues; label: string}[] = [
  {key: 'senderName', label: 'Nombre del remitente'},
  {key: 'senderEmail', label: 'Email del remitente'},
]

export const EmailSettingsForm: React.FC<Props> = ({values, resolved, showInheritance = false, saving = false, onSave}) => {
  const [form, setForm] = useState<IEmailSettingsValues>({
    senderName: values?.senderName || '',
    senderEmail: values?.senderEmail || '',
    signatureHtml: values?.signatureHtml || '',
  })
  const [overriding, setOverriding] = useState<Record<string, boolean>>({
    senderName: Boolean(values?.senderName),
    senderEmail: Boolean(values?.senderEmail),
    signatureHtml: Boolean(values?.signatureHtml),
  })

  useEffect(() => {
    setForm({
      senderName: values?.senderName || '',
      senderEmail: values?.senderEmail || '',
      signatureHtml: values?.signatureHtml || '',
    })
    setOverriding({
      senderName: Boolean(values?.senderName),
      senderEmail: Boolean(values?.senderEmail),
      signatureHtml: Boolean(values?.signatureHtml),
    })
  }, [values?.senderName, values?.senderEmail, values?.signatureHtml])

  const handleSave = () => {
    const input: IEmailSettingsValues = {}
    ;(['senderName', 'senderEmail', 'signatureHtml'] as const).forEach((key) => {
      if (showInheritance && !overriding[key]) {
        input[key] = null
      } else {
        input[key] = form[key] || null
      }
    })
    onSave(input)
  }

  const inheritedHint = (key: keyof IEmailSettingsValues) => {
    if (!showInheritance || !resolved) return null
    const source = resolved.sources?.[key]
    const value = resolved[key]
    if (!source || !value) return null
    return `Heredado de ${SOURCE_LABEL[source] || source}: ${key === 'signatureHtml' ? '(ver firma)' : value}`
  }

  return (
    <div>
      {FIELDS.map(({key, label}) => (
        <Form.Group className='mb-4' key={key}>
          <Form.Label className='fw-bold d-flex align-items-center justify-content-between'>
            <span>{label}</span>
            {showInheritance && (
              <Form.Check
                type='switch'
                id={`override-${key}`}
                label='Override'
                checked={overriding[key]}
                onChange={(e) => setOverriding((p) => ({...p, [key]: e.target.checked}))}
              />
            )}
          </Form.Label>
          <Form.Control
            value={form[key] || ''}
            onChange={(e) => setForm((p) => ({...p, [key]: e.target.value}))}
            disabled={showInheritance && !overriding[key]}
            placeholder={showInheritance && !overriding[key] ? inheritedHint(key) || 'Hereda del nivel superior' : ''}
          />
        </Form.Group>
      ))}

      <Form.Group className='mb-4'>
        <Form.Label className='fw-bold d-flex align-items-center justify-content-between'>
          <span>Firma (Email Signature)</span>
          {showInheritance && (
            <Form.Check
              type='switch'
              id='override-signatureHtml'
              label='Override'
              checked={overriding.signatureHtml}
              onChange={(e) => setOverriding((p) => ({...p, signatureHtml: e.target.checked}))}
            />
          )}
        </Form.Label>
        {showInheritance && !overriding.signatureHtml ? (
          <div className='border rounded p-3 text-muted bg-light' style={{minHeight: 120}}>
            {resolved?.signatureHtml ? (
              <>
                <div className='fs-8 mb-2'>
                  {inheritedHint('signatureHtml')}
                </div>
                <div dangerouslySetInnerHTML={{__html: resolved.signatureHtml}} />
              </>
            ) : (
              <span className='fs-8'>Sin firma configurada en ningún nivel superior.</span>
            )}
          </div>
        ) : (
          <TipTapEditor
            value={form.signatureHtml || ''}
            onChange={(html) => setForm((p) => ({...p, signatureHtml: html}))}
            height={220}
          />
        )}
      </Form.Group>

      <div className='d-flex justify-content-end pt-2 border-top'>
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </div>
    </div>
  )
}
